import { useMutation, useQuery, useQueryClient } from 'react-query'
import { notification } from 'antd'
import {
  deleteFeatureFlag,
  deleteSegment,
  getSegments,
  saveFeatureFlag,
  saveSegment,
} from '../../../domain/features/featuresApi'
import { FeatureFlagSaveInput, SegmentSaveInput } from '../../../domain/features/featureFlags'
import { useFeaturesInvalidator, useFeaturesQuery } from '../../../domain/features/featuresQueries'
import { extractApiErrorMessage } from '../../../helpers/apiErrorMessage'

const SEGMENTS_KEY = ['featureFlagSegments']

export const useFeatureFlagsQuery = () => {
  return useFeaturesQuery({
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load feature flags' })
    },
  })
}

export const useFeatureFlagSegmentsQuery = () => {
  return useQuery({
    queryKey: SEGMENTS_KEY,
    queryFn: getSegments,
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load segments' })
    },
  })
}

export const useSaveFeatureFlagMutation = () => {
  const { invalidateFeatures } = useFeaturesInvalidator()
  return useMutation(
    ({ id, input }: { id: string; input: FeatureFlagSaveInput }) => saveFeatureFlag(id, input),
    {
      onSuccess: (_, { id }) => {
        invalidateFeatures()
        notification.success({ message: `Feature flag '${id}' saved` })
      },
    }
  )
}

export const useDeleteFeatureFlagMutation = () => {
  const { invalidateFeatures } = useFeaturesInvalidator()
  return useMutation((id: string) => deleteFeatureFlag(id), {
    onSuccess: (_, id) => {
      invalidateFeatures()
      notification.success({ message: `Feature flag '${id}' deleted` })
    },
    onError: (error) => {
      notification.error({ message: extractApiErrorMessage(error, 'Failed to delete feature flag') })
    },
  })
}

export const useSaveSegmentMutation = () => {
  const queryClient = useQueryClient()
  const { invalidateFeatures } = useFeaturesInvalidator()
  return useMutation(
    ({ id, input }: { id: string; input: SegmentSaveInput }) => saveSegment(id, input),
    {
      onSuccess: (_, { input }) => {
        queryClient.invalidateQueries(SEGMENTS_KEY)
        invalidateFeatures()
        notification.success({ message: `Segment '${input.name}' saved` })
      },
    }
  )
}

export const useDeleteSegmentMutation = () => {
  const queryClient = useQueryClient()
  const { invalidateFeatures } = useFeaturesInvalidator()
  return useMutation((id: string) => deleteSegment(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(SEGMENTS_KEY)
      invalidateFeatures()
      notification.success({ message: 'Segment deleted' })
    },
    onError: (error) => {
      notification.error({ message: extractApiErrorMessage(error, 'Failed to delete segment') })
    },
  })
}
